import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './state/AuthContext';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DealersPage from './pages/DealersPage';
import TenantsPage from './pages/TenantsPage';
import AdminLayout from './components/AdminLayout';
import UsersPage from './pages/UsersPage';
import MetricsPage from './pages/MetricsPage';
import ForcePasswordChangePage from './pages/ForcePasswordChangePage';
import ApplicationsPage from './pages/ApplicationsPage';
import AuditLogsPage from './pages/AuditLogsPage';

import TicketsPage from './pages/TicketsPage';
import FaqPage from './pages/FaqPage';
import ProfilePage from './pages/ProfilePage';
import ActiveSessionsPage from './pages/ActiveSessionsPage';
import RegisteredDevicesPage from './pages/RegisteredDevicesPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import HealthDashboardPage from './pages/HealthDashboardPage';

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const { isAuthenticated, isInitializing, user } = useAuth();

  if (isInitializing) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // tenant_user has no access to the License Manager admin panel
  if (user?.role === 'tenant_user') {
    return <Navigate to="/login" replace />;
  }

  if (user?.mustChangePassword && location.pathname !== '/force-password-change') {
    return <Navigate to="/force-password-change" replace />;
  }
  if (!user?.mustChangePassword && location.pathname === '/force-password-change') {
    return <Navigate to="/metrics" replace />;
  }
  return children;
}

function PermissionRoute({ children, roles }: { children: JSX.Element; roles: string[] }) {
  const { user } = useAuth();
  if (!user || (roles.length > 0 && !roles.includes(user.role))) {
    return <Navigate to="/" replace />;
  }
  return children;
}

export default function App() {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return null; // Show loading screen if needed
  }

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
        />
        <Route
          path="/reset-password"
          element={isAuthenticated ? <Navigate to="/" replace /> : <ResetPasswordPage />}
        />
        <Route
          path="/privacy-policy"
          element={isAuthenticated ? <Navigate to="/" replace /> : <PrivacyPolicy />}
        />
        <Route
          path="/force-password-change"
          element={
            <ProtectedRoute>
              <ForcePasswordChangePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="metrics" replace />} />
          <Route path="metrics" element={<MetricsPage />} />
          <Route
            path="dealers"
            element={
              <PermissionRoute roles={['master_full']}>
                <DealersPage />
              </PermissionRoute>
            }
          />
          <Route
            path="applications"
            element={
              <PermissionRoute roles={['master_full', 'dealer_admin']}>
                <ApplicationsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="tenants"
            element={
              <PermissionRoute roles={['master_full', 'dealer_admin']}>
                <TenantsPage />
              </PermissionRoute>
            }
          />

          <Route
            path="users"
            element={
              <PermissionRoute roles={['master_full', 'dealer_admin', 'tenant_admin']}>
                <UsersPage />
              </PermissionRoute>
            }
          />
          <Route
            path="audit-logs"
            element={
              <PermissionRoute roles={['master_full']}>
                <AuditLogsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="sessions"
            element={
              <PermissionRoute roles={['master_full', 'dealer_admin', 'tenant_admin']}>
                <ActiveSessionsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="devices"
            element={
              <PermissionRoute roles={['master_full', 'tenant_admin']}>
                <RegisteredDevicesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="tickets"
            element={
              <PermissionRoute roles={['master_full', 'dealer_admin', 'tenant_admin']}>
                <TicketsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="faq"
            element={
              <PermissionRoute roles={['master_full', 'dealer_admin', 'tenant_admin']}>
                <FaqPage />
              </PermissionRoute>
            }
          />
          <Route
            path="profile"
            element={
              <PermissionRoute roles={['master_full', 'dealer_admin', 'tenant_admin']}>
                <ProfilePage />
              </PermissionRoute>
            }
          />
          <Route
            path="health"
            element={
              <PermissionRoute roles={['master_full', 'dealer_admin', 'tenant_admin']}>
                <HealthDashboardPage />
              </PermissionRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
